import { useEffect, useRef } from 'react';
import { type IFramework } from '@/framework';
import { getLocaleStore } from '@/i18n';
import type { QuickStageOperate } from '@/plugins/apps/user-editor/stage-operate';
import type { MouseOperate } from '@/plugins/mouse-operate';
import type { ShortcutMenu } from '@/plugins/shortcut-menu';
import { BaseLayerViewType } from '@/version-types';

/** 键盘操作态 class name */
const KEYBOARD_OPERATE_CLASSNAME = 'keyboard-operate';

export function useMoveLayerHotkey(framework: IFramework) {
    const moveTimeoutHandle = useRef<NodeJS.Timeout>();

    useEffect(() => {
        const { $t } = getLocaleStore();

        const mouseDownHandle = framework.service.editor.onMouseDown(() => {
            document.body.classList.remove(KEYBOARD_OPERATE_CLASSNAME);
        }, 9);

        const moveLayer = (x: number, y: number) => {
            const activeLayer = framework.service.frame.getActivePickLayer();
            if (framework.service.editor.disableMove || !activeLayer) return false;
            if (activeLayer.lock) return true;
            const activeNode = document.querySelector('.active-operate');
            if (activeNode) {
                document.body.classList.add(KEYBOARD_OPERATE_CLASSNAME);
            }
            const layerId = activeLayer.id;
            const oldLayer = framework.service.flatLayer.getLayer(layerId, true);
            framework.service.registration.getInstance<ShortcutMenu>('ShortcutMenu')?.setVisible(false);
            framework.service.frame.__emitBeforeBaseOperate(oldLayer, BaseLayerViewType.MOVE);
            const originLayerInfo = framework.service.frame.exportLayerOriginInfo(layerId);
            const { originInfo, childOriginInfoMap } = originLayerInfo;
            framework.service.registration.getInstance<MouseOperate>('MouseOperate')?.clearSelectionOperate();
            framework.service.frame.updateLayerPosition(layerId, x, y, originInfo!, childOriginInfoMap, {
                isStart: false,
                isEnd: false,
            });
            clearTimeout(moveTimeoutHandle.current);
            moveTimeoutHandle.current = setTimeout(() => {
                framework.service.frame.updateLayerPosition(layerId, x, y, originInfo!, childOriginInfoMap, {
                    isStart: false,
                    isEnd: true,
                });
                document.body.classList.remove(KEYBOARD_OPERATE_CLASSNAME);
                framework.service.frame.addOrUpdateLayer(framework.service.flatLayer.getLayer(layerId, true));
                framework.service.history.write(oldLayer);
                framework.service.frame.setActiveLayer(layerId);
                framework.service.registration.getInstance<ShortcutMenu>('ShortcutMenu')?.setVisible(true);
            }, 500);
            return true;
        };

        const changeStage = (isDown: boolean) => {
            if (isDown) {
                framework.service.registration.getInstance<QuickStageOperate>('QuickStageOperate')?.down();
            } else {
                framework.service.registration.getInstance<QuickStageOperate>('QuickStageOperate')?.up();
            }
        };

        const moveDistance = 1;
        const quickMoveDistance = 10;

        framework.service.hotkey.register({
            name: $t((v) => v.Text_Move),
            win: '↑↓←→',
            mac: '↑↓←→',
            key: 'move',
            processors: [
                {
                    key: 'up',
                    press: ['Up'],
                    fn: () => {
                        if (!moveLayer(0, -moveDistance)) {
                            changeStage(false);
                        }
                    },
                },
                {
                    key: 'right',
                    press: ['Right'],
                    fn: () => {
                        if (!moveLayer(moveDistance, 0)) {
                            changeStage(true);
                        }
                    },
                },
                {
                    key: 'down',
                    press: ['Down'],
                    fn: () => {
                        if (!moveLayer(0, moveDistance)) {
                            changeStage(true);
                        }
                    },
                },
                {
                    key: 'left',
                    press: ['Left'],
                    fn: () => {
                        if (!moveLayer(-moveDistance, 0)) {
                            changeStage(false);
                        }
                    },
                },
            ],
        });

        framework.service.hotkey.register({
            name: $t((v) => v.Text_MoveWithCombinedKeys),
            win: 'Shift ↑↓←→',
            mac: '⇧ ↑↓←→',
            key: 'quickMove',
            processors: [
                {
                    key: 'up',
                    press: ['Shift+Up'],
                    fn: () => {
                        if (!moveLayer(0, -quickMoveDistance)) {
                            changeStage(false);
                        }
                    },
                },
                {
                    key: 'right',
                    press: ['Shift+Right'],
                    fn: () => {
                        if (!moveLayer(quickMoveDistance, 0)) {
                            changeStage(true);
                        }
                    },
                },
                {
                    key: 'down',
                    press: ['Shift+Down'],
                    fn: () => {
                        if (!moveLayer(0, quickMoveDistance)) {
                            changeStage(true);
                        }
                    },
                },
                {
                    key: 'left',
                    press: ['Shift+Left'],
                    fn: () => {
                        if (!moveLayer(-quickMoveDistance, 0)) {
                            changeStage(false);
                        }
                    },
                },
            ],
        });

        return () => {
            framework.service.events.off(mouseDownHandle);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅挂载时注册，与拆分前一致
    }, []);
}
